import React, { useRef, useState, useCallback, useMemo } from "react";
import { Menu, CloseButton, Group, Tooltip } from "@mantine/core";
import {
  IconBrandGoogleDrive,
  IconStack3,
  IconUpload,
} from "@tabler/icons-react";
import useGoogleDrive from "./GoogleDriveOperations";
import useDropbox from "./DropboxOperations";
import FileList from "./FilesDisplay";
import useStartupStore from "../../stores/startupStore";
import useUserStore from "../../stores/userStore";
import styled from "styled-components";
import Mixpanel from "./mixpanel";
import { useTranslation } from "react-i18next";
import { t } from "i18next";


const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 15px;
  border: 1px solid var(--color-button-accent);
  border-radius: 4px;
  color: var(--color-button-accent);
  fontweight: var(--font-weight-body-1);
  cursor: pointer;
  text-align: center;
  background: transparent;
  &:hover {
    background-color: var(--color-button-accent);
    color: var(--color-container-card-default);
  }
  width: ${({ $fullwidth }) => ($fullwidth ? "100%" : "unset")};
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Custom styled file input that looks like a menu item
const FileInputLabel = styled.label`
  display: flex;
  align-items: center;
  padding: 6px 10px;
  width: 100%;
  font-size: 14px;
  cursor: pointer;
  color: #000;

  &:hover {
    background-color: #f8f9fa;
  }

  input {
    display: none;
  }
`;

// Progress bar component
const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background-color: #e9ecef;
  border-radius: 2px;
  margin-top: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: var(--color-button-accent);
  width: ${(props) => props.progress}%;
  transition: width 0.3s ease;
`;

const UploadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 8px;
`;

const CustomFileUploader = ({ appId }) => {
  // Using global store for file state
  
  const {
    files,
    setFiles,
    selectedProgram: { cid: pageId, sid: index },
    program,
    isOwner,
    isMember,
    submitPendingChanges,
    isSubmitted
  } = useStartupStore((state) => ({
    files: state.files,
    setFiles: state.setFiles,
    selectedProgram: state.selectedProgram,
    program: state.program,
    isOwner: state.isOwner,
    isMember: state.isMember,
    submitPendingChanges: state.submitPendingChanges,
    isSubmitted: state.isSubmitted
  }));

  const fileInputRef = useRef(null);
  const [opened, setOpened] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [uploading, setUploading] = useState([]);
  const { user: userPayload, setUser } = useUserStore((state) => ({
    user: state.user,
    setUser: state.setUser,
  }));

  const storageLimitState = userPayload?.limit_states?.storage || {};
  const storageLimitReached = Boolean(storageLimitState.limit_reached);
  const storageLimitRemaining = storageLimitState.remaining;
  const storageLimitMessage = useMemo(() => {
    if (!storageLimitState.limit) return "Storage limit reached.";
    const usedMB = (storageLimitState.used / (1024 * 1024)).toFixed(0);
    const limitMB = (storageLimitState.limit / (1024 * 1024)).toFixed(0);
    return `You've used ${usedMB}MB of ${limitMB}MB. Delete files or upgrade to free up space.`;
  }, [storageLimitState]);

  const updateStorageUsage = useCallback(
    (usage) => {
      if (!usage) return;
      setUser({
        limit_states: {
          ...(userPayload?.limit_states || {}),
          storage: usage,
        },
      });
    },
    [setUser, userPayload]
  );

  const handleStorageLimitError = useCallback(
    (payload) => {
      if (payload?.storage_usage) {
        updateStorageUsage(payload.storage_usage);
      }
      const message =
        payload?.error ||
        storageLimitMessage ||
        "You've reached your storage limit.";
      alert(message);
    },
    [updateStorageUsage, storageLimitMessage]
  );

  // Configuration values
  const clientId =
    "646888565719-k2u21847fotu41ud9eagk3rcs18hk9v5.apps.googleusercontent.com";
  const apiKey = "AIzaSyAyrRvP9x6xqL8v3LS82O6AYjvlBvNnsJE";
  const dropboxAppKey = "7u7qdb2r7djclry";
  const dropboxRedirectUri = "https://nowcollab.com/dashboard";

  const subcategoryId = program.categories[pageId]?.sub_categories[index]?.id;

  // API endpoints
  const signedUrlEndpoint = `/uploads/signed_url`;
  const confirmUploadEndpoint = `/uploads/confirm`;
  const deleteFileUrl = `/uploads/remove`;

  // Initialize hooks for Google Drive and Dropbox
  const { isGoogleAuthenticated, createPicker, silentAuthenticate } =
    useGoogleDrive({ clientId, apiKey, appId });
  const {
    authenticateDropbox,
    openDropboxChooser,
    isDropboxAuthenticated,
    fetchDropboxThumbnail,
  } = useDropbox(dropboxAppKey, dropboxRedirectUri);

  // Helper functions to build preview URLs
  const getGoogleDrivePreviewUrl = (docId) =>
    `https://drive.google.com/file/d/${docId}/preview`;
  const getDropboxPreviewUrl = (link) => link.replace("?dl=0", "?raw=1");

  // Function to update upload progress
  const updateUploadProgress = useCallback(
    (fileId, progress) => {
      setUploading((prev) =>
        prev.map((file) => (file.id === fileId ? { ...file, progress } : file))
      );

      // Also update in the files state if present
      const existingFiles = files[pageId]?.[index] || [];
      const updatedFiles = existingFiles.map((f) => {
        if (f.id === fileId && f.status === "uploading") {
          return { ...f, progress };
        }
        return f;
      });

      if (JSON.stringify(updatedFiles) !== JSON.stringify(existingFiles)) {
        setFiles(updatedFiles);
      }
    },
    [files, pageId, index, setFiles]
  );

  // Handler for custom file upload with progress tracking
  const uploadFileToGCS = async (file) => {
    // Generate a temporary ID for tracking this upload
    const tempId = `temp-${file.name}-${Date.now()}`;

    try {
      if (storageLimitState.limit && typeof storageLimitRemaining === "number") {
        if (file.size > storageLimitRemaining) {
          alert(
            `Uploading "${file.name}" would exceed your storage limit. Please delete files or upgrade.`
          );
          return;
        }
      }

      // Add to uploading state
      const newUploadingFile = {
        id: tempId,
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 0,
      };

      setUploading((prev) => [...prev, newUploadingFile]);

      // Add to files state as "uploading"
      const tempFile = {
        id: tempId,
        name: file.name,
        size: file.size,
        type: file.type,
        source: "gcs",
        status: "uploading",
        progress: 0,
      };

      const existingFiles = files[pageId]?.[index] || [];
      setFiles([...existingFiles, tempFile]);

      // Get CSRF token
      const token = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");

      // STEP 1: Get signed URL
      const signedUrlResponse = await fetch(signedUrlEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
        },
        body: JSON.stringify({
          filename: file.name,
          content_type: file.type,
          context_type: "SubCategory", // Indicate the type of context
          context_id: subcategoryId,
        }),
      });

      const signedUrlPayload = await signedUrlResponse.json().catch(() => ({}));

      if (!signedUrlResponse.ok) {
        if (signedUrlPayload?.error_code === "limit_exceeded") {
          handleStorageLimitError(signedUrlPayload);
        }
        throw new Error(signedUrlPayload.error || "Failed to get signed URL");
      }

      const { signed_url, file_key } = signedUrlPayload;

      // STEP 2: Upload to GCS with progress tracking
      const xhr = new XMLHttpRequest();

      // Set up progress tracking
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round(
            (event.loaded / event.total) * 100
          );
          updateUploadProgress(tempId, percentComplete);
        }
      };

      // Create a promise to track completion
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.onload = async () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            updateUploadProgress(tempId, 100);

            try {
              // STEP 3: Confirm upload with backend
              const confirmResponse = await fetch(confirmUploadEndpoint, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-CSRF-Token": token,
                },
                body: JSON.stringify({
                  file_key,
                  filename: file.name,
                  content_type: file.type,
                  size_bytes: file.size,
                  attachable_type: "SubCategory", // Indicate the type Attachment links to
                  attachable_id: subcategoryId,
                }),
              });

              const confirmPayload = await confirmResponse.json().catch(() => ({}));

              if (!confirmResponse.ok) {
                if (confirmPayload?.error_code === "limit_exceeded") {
                  handleStorageLimitError(confirmPayload);
                }
                reject(
                  new Error(confirmPayload.error || "Failed to confirm upload")
                );
                return;
              }

              const fileData = confirmPayload;
              resolve(fileData);
            } catch (error) {
              reject(error);
            }
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        };

        xhr.onerror = () => {
          reject(new Error("Network error during upload"));
        };
      });

      // Start the upload
      xhr.open("PUT", signed_url, true);
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.send(file);

      // Wait for upload to complete
      const result = await uploadPromise;

      // On success, update file in state
      if (result && result.file) {
        const confirmedFile = result.file;

        console.log(confirmedFile);

        // Update the file in our state
        const existingFiles = files[pageId]?.[index] || [];
        setFiles([
          ...existingFiles,
          {
            id: confirmedFile.id,
            name: confirmedFile.name,
            size_bytes: confirmedFile.size_bytes,
            type: confirmedFile.content_type,
            url: confirmedFile.url[0],
            source: "gcs",
            status: "ready",
            progress: 100,
            isNew: true,
          },
        ]);

        submitPendingChanges();

        updateStorageUsage(result.storage_usage);

        // Remove from uploading state
        setUploading((prev) => prev.filter((f) => f.id !== tempId));

        return result;
      }
    } catch (error) {
      console.error("Upload failed:", error);

      // Update file status to failed
      const existingFiles = files[pageId]?.[index] || [];
      const updatedFiles = existingFiles.map((f) => {
        if (f.id === tempId) {
          return {
            ...f,
            status: "failed",
            error: error.message || "Upload failed",
          };
        }
        return f;
      });

      setFiles(updatedFiles);

      // Remove from uploading state
      setUploading((prev) => prev.filter((f) => f.id !== tempId));

      throw error;
    }
  };

  // Handle manual file selection
  const handleFileInputChange = async (event) => {
    const selectedFiles = Array.from(event.target.files);

    // Validate files
    const validFiles = selectedFiles.filter((file) => {
      if (file.size <= 10 * 1024 * 1024) {
        // 10MB limit
        return true;
      }
      alert(
        `File "${file.name}" exceeds the 10MB limit and will not be uploaded.`
      );
      return false;
    });

    if (validFiles.length === 0) return;

    // Upload all valid files
    for (const file of validFiles) {
      try {
        await uploadFileToGCS(file);
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
      }
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Close the menu after selection
    setOpened(false);
  };

  // Handler for deleting files
  const handleDeleteFile = async (fileToDelete) => {
    try {
      setIsDeleting(true);

      // Get CSRF token from meta tag for Rails
      const token = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");

      const response = await fetch(deleteFileUrl, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
        },
        body: JSON.stringify({
          fileId: fileToDelete.id,
          sub_category_id: subcategoryId,
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.error || "Failed to delete file");
      }

      // Remove file from state regardless of source
      const existingFiles = files[pageId]?.[index] || [];
      const updatedFiles = existingFiles.filter(
        (file) => file.id !== fileToDelete.id
      );

      // Update files in state
      setFiles(updatedFiles);
      updateStorageUsage(payload.storage_usage);

      return true;
    } catch (error) {
      console.error("Error deleting file:", error);
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  // Handler for Google Drive file selection
  const handleGoogleDrivePick = () => {
    createPicker((data) => {
      if (
        data[window.google.picker.Response.ACTION] ===
        window.google.picker.Action.PICKED
      ) {
        const pickedFiles = data[window.google.picker.Response.DOCUMENTS].map(
          (doc) => {
            const thumbnailUrl = `https://drive.google.com/thumbnail?authuser=0&sz=w320&id=${doc.id}`;
            return {
              id: doc.id,
              name: doc.name,
              size_bytes: doc.sizeBytes || 0,
              mimeType: doc.mimeType,
              icon: doc.iconUrl,
              url: getGoogleDrivePreviewUrl(doc.id),
              source: "drive",
              isNew: true,
              thumbnail: thumbnailUrl,
            };
          }
        );
        const existingFiles = files[pageId]?.[index] || [];
        setFiles([...existingFiles, ...pickedFiles]);
        submitPendingChanges();
        setOpened(false);
      }
    });
  };

  // Handler for Dropbox file selection
  const handleDropboxPick = () => {
    openDropboxChooser(async (filesFromDropbox) => {
      const selectedFiles = await Promise.all(
        filesFromDropbox.map(async (file) => {
          let thumbnailUrl = null;
          if (file.path_lower) {
            try {
              thumbnailUrl = await fetchDropboxThumbnail(file.path_lower);
            } catch (error) {
              console.error(
                "Error fetching thumbnail for Dropbox file:",
                file.name,
                error
              );
              thumbnailUrl = file.icon;
            }
          } else {
            thumbnailUrl = file.icon;
          }
          return {
            id: file.id,
            name: file.name,
            size_bytes: file.bytes,
            isNew: true,
            url: getDropboxPreviewUrl(file.link),
            source: "dropbox",
            icon: file.icon,
            thumbnail: thumbnailUrl || file.icon,
          };
        })
      );
      const existingFiles = files[pageId]?.[index] || [];
      setFiles([...existingFiles, ...selectedFiles]);
      submitPendingChanges();
      setOpened(false);
    });
  };

  return (
    <>
      {/* Upload box is always visible */}
      {((isOwner || isMember) && !isSubmitted) && (
        <Menu
          transitionProps={{ transition: "rotate-right", duration: 150 }}
          shadow="md"
          width="target"
          // withinPortal
          position="top-end"
          zIndex={3000}
          offset={4}
          positionDependencies={[opened]}
          opened={opened}
        >
          <Menu.Target>
            <Button
              $fullwidth
              onClick={() => {
                setOpened((o) => {
                  const newState = !o;
                  return newState;
                });
              }}
            >
             {t("attach_files")}
              <span style={{ marginLeft: "8px" }}>+</span>
            </Button>
          </Menu.Target>

          <Menu.Dropdown>
            <Group position="apart">
              <Menu.Label>Connected apps</Menu.Label>
              <CloseButton
                onClick={() => {
                  setOpened(false);
                }}
              />
            </Group>

            {/* Google Drive Option */}
            {!isGoogleAuthenticated ? (
              <Menu.Item
                icon={<IconBrandGoogleDrive size={14} />}
                onClick={() => {
                  silentAuthenticate();
                  Mixpanel.button("Authenticate with Google Drive", {
                    location: "Attach Files Menu",
                    auth_status_before: "unauthenticated",
                  });
                }}
              >
                <p>Authenticate with Google Drive</p>
              </Menu.Item>
            ) : (
              <Menu.Item
                icon={<IconBrandGoogleDrive size={14} />}
                onClick={() => {
                  handleGoogleDrivePick();
                  Mixpanel.button("Choose from Google Drive", {
                    location: "Attach Files Menu",
                    auth_status: "authenticated",
                  });
                }}
              >
                <p>Choose from Google Drive</p>
              </Menu.Item>
            )}

            {/* Dropbox Option */}
            {!isDropboxAuthenticated ? (
              <Menu.Item
                icon={<IconStack3 size={14} />}
                onClick={() => {
                  authenticateDropbox();
                  Mixpanel.button("Authenticate with Dropbox", {
                    location: "Attach Files Menu",
                    auth_status_before: "unauthenticated",
                  });
                }}
              >
                <p>Authenticate with Dropbox</p>
              </Menu.Item>
            ) : (
              <Menu.Item
                icon={<IconStack3 size={14} />}
                onClick={() => {
                  handleDropboxPick();
                  Mixpanel.button("Choose from Dropbox", {
                    location: "Attach Files Menu",
                    auth_status: "authenticated",
                  });
                }}
              >
                <p>Choose from Dropbox</p>
              </Menu.Item>
            )}

            <Menu.Divider />

            {/* Custom file upload option */}
            <Menu.Item p={0}>
              <FileInputLabel
                onClick={(e) => {
                  if (storageLimitReached) {
                    e.preventDefault();
                    alert(storageLimitMessage);
                  }
                }}
                style={{
                  opacity: storageLimitReached ? 0.5 : 1,
                  cursor: storageLimitReached ? "not-allowed" : "pointer",
                }}
              >
                <IconUpload size={14} style={{ marginRight: "8px" }} />
                <span>Upload from your device</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".doc,.docx,.xls,.xlsx,.pdf,.zip,.rar,.jpg,.jpeg,.png,.gif"
                  disabled={storageLimitReached}
                  onChange={(e) => {
                    handleFileInputChange(e);
                    if (e.target.files.length > 0) {
                      Mixpanel.button("Upload from Device - Files Selected", {
                        location: "Attach Files Menu",
                        num_files_selected: e.target.files.length,
                      });
                    }
                  }}
                />
              </FileInputLabel>
            </Menu.Item>
            {storageLimitReached && (
              <Menu.Label style={{ color: "#e03131", whiteSpace: "normal" }}>
                {storageLimitMessage}
              </Menu.Label>
            )}

            {/* Show uploading files if any */}
            {uploading.length > 0 && (
              <>
                <Menu.Divider />
                <Menu.Label>Uploading</Menu.Label>
                {uploading.map((file) => (
                  <UploadingWrapper key={file.id}>
                    <span style={{ fontSize: "12px" }}>{file.name}</span>
                    <ProgressBar>
                      <ProgressFill progress={file.progress} />
                    </ProgressBar>
                  </UploadingWrapper>
                ))}
              </>
            )}
          </Menu.Dropdown>
        </Menu>
      )}

      {/* File list displayed below the upload box */}
      <div
        style={{
          overflowX: "auto",
          whiteSpace: "nowrap",
          marginTop: "1rem",
        }}
      >
        <FileList
          files={files[pageId]?.[index] || []}
          onDeleteFile={handleDeleteFile}
          isDeleting={isDeleting}
          canDelete={(isOwner || isMember) && !isSubmitted}
        />
      </div>
    </>
  );
};

export default CustomFileUploader;
